import {
  handleBreakpoints,
  keyframes,
  type ResponsiveStyleValue,
} from '@mui/system';
import { pxToRem } from '@packages/themes/utils';
import { makeStyles } from '../styles/tss-react';
import { ButtonSize } from './types';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

interface StyleProps {
  size: ResponsiveStyleValue<ButtonSize>;
}

export const useStyles = makeStyles<StyleProps>()(
  ({ palette, breakpoints, typography }, { size }) => {
    const getIconSize = (iconSize: number) => ({
      '& > .MuiButton-icon svg': {
        width: pxToRem(iconSize),
        height: pxToRem(iconSize),
      },
    });

    const getSizeStyles = (buttonSize: ButtonSize) => {
      switch (buttonSize) {
        case ButtonSize.XS:
          return {
            padding: `${pxToRem(4)} ${pxToRem(16)}`,
            '&.MuiIconButton-root': {
              padding: pxToRem(8),
            },
            ...getIconSize(16),
          };
        case ButtonSize.SM:
          return {
            padding: `${pxToRem(8)} ${pxToRem(24)}`,
            '&.MuiIconButton-root': {
              padding: pxToRem(8),
            },
            ...getIconSize(24),
          };
        default:
          return {
            padding: `${pxToRem(16)} ${pxToRem(24)}`,
            '&.MuiIconButton-root': {
              padding: pxToRem(16),
            },
            ...getIconSize(24),
          };
      }
    };

    return {
      loadingIcon: {
        display: 'inline-flex',
        animation: `${spin} 0.8s linear infinite`,
      },
      root: {
        height: 'fit-content',
        border: '1px solid',
        gap: pxToRem(8),
        borderRadius: pxToRem(32),
        fontSize: pxToRem(18),
        lineHeight: pxToRem(24),
        letterSpacing: pxToRem(-0.3),
        fontWeight: typography.fontWeightRegular,
        textTransform: 'none',
        fontFamily: 'Roobert, sans-serif',
        ...handleBreakpoints({ theme: { breakpoints } }, size, (resolvedSize) =>
          getSizeStyles(resolvedSize as ButtonSize)
        ),

        '& > .MuiButton-icon': {
          display: 'inherit',
          margin: 0,
          opacity: `1 !important`,
        },

        // primary variant default, hover, active, focus and disabled styles
        '&.MuiButton-primary, &.MuiIconButton-primary': {
          color: palette.semantic.text.buttonPrimary,
          borderColor: palette.semantic.button.primary,
          backgroundColor: palette.semantic.button.primary,
          fontWeight: typography.fontWeightSemibold,

          '&:hover': {
            backgroundColor: palette.semantic.button.hoverPrimary,
            borderColor: palette.semantic.button.hoverPrimary,
          },
          '&:active': {
            backgroundColor: palette.semantic.button.activePrimary,
            borderColor: palette.semantic.button.activePrimary,
          },
          '&:disabled': {
            opacity: 0.5,
          },
          '&.Mui-focusVisible, &:focus-visible': {
            backgroundColor: palette.semantic.button.primary,
          },
        },

        // secondary variant default, hover, active, focus and disabled styles
        '&.MuiButton-secondary, &.MuiIconButton-secondary': {
          color: palette.semantic.text.buttonSecondary,
          borderColor: palette.semantic.button.border,
          backgroundColor: palette.semantic.button.secondary,

          '&:hover': {
            backgroundColor: palette.semantic.button.hoverSecondary,
          },
          '&:active': {
            backgroundColor: palette.semantic.button.activeSecondary,
          },
          '&:disabled': {
            opacity: 0.5,
          },
          '&.Mui-focusVisible, &:focus-visible': {
            backgroundColor: palette.semantic.button.secondary,
          },
        },

        // tertiary variant — outlined
        '&.MuiButton-tertiary, &.MuiIconButton-tertiary': {
          color: palette.semantic.text.buttonSecondary,
          borderColor: palette.semantic.button.border,
          backgroundColor: 'transparent',

          '&:hover': {
            backgroundColor: palette.semantic.button.hoverSecondary,
          },
          '&:active': {
            backgroundColor: palette.semantic.button.activeSecondary,
          },
          '&:disabled': {
            opacity: 0.5,
          },
          '&.Mui-focusVisible, &:focus-visible': {
            backgroundColor: 'transparent',
          },
        },

        // ghost — no border or fill
        '&.MuiButton-ghost, &.MuiIconButton-ghost': {
          color: palette.semantic.text.buttonSecondary,
          borderColor: 'transparent',
          backgroundColor: 'transparent',

          '&:hover': {
            backgroundColor: palette.semantic.button.hoverSecondary,
          },
          '&:active': {
            backgroundColor: palette.semantic.button.activeSecondary,
          },
          '&:disabled': {
            opacity: 0.5,
          },
          '&.Mui-focusVisible, &:focus-visible': {
            backgroundColor: 'transparent',
          },
        },

        '&.MuiButton-inline': {
          display: 'inline',
          verticalAlign: 'baseline',
          color: 'inherit',
          font: 'inherit',
          letterSpacing: 'inherit',
          textAlign: 'inherit',
          whiteSpace: 'inherit',
          textTransform: 'inherit',
          textDecoration: 'underline',
          padding: 0,
          minWidth: 0,
          borderRadius: pxToRem(4),
          textUnderlineOffset: '2px', // must be in pixels
          border: 'none',
          borderColor: 'transparent',
          backgroundColor: 'transparent',

          '&:disabled, &.Mui-disabled': {
            opacity: 0.5,
            color: 'inherit',
          },
        },

        // focus ring — 1px border + 1px inset shadow so size stays put
        '&.Mui-focusVisible, &:focus-visible': {
          borderColor: palette.semantic.button.borderFocus,
          boxShadow: `inset 0 0 0 1px ${palette.semantic.button.borderFocus}`,
        },
      },
    };
  }
);
